import { CheckCircle, Info, TriangleAlert, X } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/button";
import type { ToastAction, ToastVariant } from "./types";

const icons = {
  success: CheckCircle,
  error: TriangleAlert,
  information: Info,
} as const;

export interface ToastUIProps {
  id: string | number;
  title: string;
  description?: string;
  variant: ToastVariant;
  action?: ToastAction;
  className?: string;
}

export function ToastUI({
  id,
  title,
  description,
  variant,
  action,
  className,
}: ToastUIProps) {
  const Icon = icons[variant];

  return (
    <div
      data-testid="toast"
      data-variant={variant}
      className={cn(
        "relative flex w-full flex-col rounded-3xl p-6 shadow-lg md:w-[504px]",
        "bg-brand-primary-500 text-gray-900",
        className,
      )}
    >
      <button
        type="button"
        className="absolute top-6 right-6 cursor-pointer hover:opacity-70"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <X size={24} />
      </button>

      <div className="flex items-start gap-2 pr-8">
        <Icon size={24} className="shrink-0 text-gray-900" />
        <p className="text-lg font-bold">{title}</p>
      </div>

      {description && (
        <p className="mt-2 text-base opacity-90">{description}</p>
      )}

      {action && (
        <Button
          type="button"
          variant="link"
          data-testid="toast-action"
          className="mt-3 h-auto self-start p-0 text-base font-bold text-gray-900 underline hover:font-bold hover:opacity-70 dark:text-gray-900"
          onClick={() => {
            action.onClick();
            sonnerToast.dismiss(id);
          }}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
